import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";

const SIZE_LABELS = {
  digital: "Digital Download",
  small: "Small Print (12×18)",
  large: "Large Print (24×36)",
};

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, cartTotal } = useCart();

  if (cartItems.length === 0) {
    return (
      <>
        <Helmet>
          <title>Cart - limkc.com</title>
        </Helmet>
        <div className="w-full h-full overflow-hidden">
        <div
          className="px-4 py-12 min-h-screen"
          style={{
            paddingTop: 'calc(var(--navbar-height) + 5rem)',
            backgroundColor: 'transparent',
            color: 'var(--text-primary)',
          }}
        >
          <div className="max-w-2xl mx-auto text-center">
              <h1 className="text-3xl font-bold tracking-tight sm:text-4xl mb-12" style={{ color: 'var(--text-primary)' }}>
                Shopping Cart
              </h1>
              <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>Your cart is empty</p>
              <Link
                to="/arts"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Continue Shopping
                <span aria-hidden="true"> &rarr;</span>
              </Link>
            </div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Cart - limkc.com</title>
      </Helmet>
      <div className="w-full h-full overflow-hidden">
        <div
          className="px-4 py-12 min-h-screen"
          style={{
            paddingTop: 'calc(var(--navbar-height) + 5rem)',
            backgroundColor: 'transparent',
            color: 'var(--text-primary)',
          }}
        >
          <div className="max-w-2xl mx-auto">
            <h1 className="text-center text-3xl font-bold tracking-tight sm:text-4xl mb-12" style={{ color: 'var(--text-primary)' }}>
              Shopping Cart
            </h1>

            <section aria-labelledby="cart-heading">
              <h2 id="cart-heading" className="sr-only">Items in your shopping cart</h2>

              <ul
                className="divide-y"
                style={{ borderColor: 'var(--bg-secondary)', borderTopWidth: '1px', borderBottomWidth: '1px' }}
              >
                {cartItems.map((item) => (
                  <li key={item.itemId} className="flex py-6">
                    <div className="shrink-0">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="size-24 rounded-md object-cover sm:size-32"
                        style={{ backgroundColor: 'var(--bg-secondary)' }}
                        onError={(e) => {
                          e.target.src = `https://placehold.co/128x128/1a1a2e/ffffff?text=${encodeURIComponent(item.title)}`;
                        }}
                      />
                    </div>

                    <div className="ml-4 flex flex-1 flex-col sm:ml-6">
                      <div>
                        <div className="flex justify-between">
                          <h4 className="text-sm">
                            <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                              {item.title}
                            </span>
                          </h4>
                          <p className="ml-4 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            ${item.price * item.quantity}
                          </p>
                        </div>
                        <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {SIZE_LABELS[item.size]}
                        </p>
                        <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {item.edition}
                        </p>
                      </div>

                      <div className="mt-4 flex flex-1 items-end justify-between">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.itemId, item.quantity - 1)}
                            className="w-8 h-8 flex items-center justify-center rounded transition hover:opacity-75"
                            style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                          >
                            -
                          </button>
                          <span className="w-8 text-center" style={{ color: 'var(--text-primary)' }}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.itemId, item.quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center rounded transition hover:opacity-75"
                            style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                          >
                            +
                          </button>
                        </div>
                        <div className="ml-4">
                          <button
                            type="button"
                            onClick={() => removeFromCart(item.itemId)}
                            className="text-sm font-medium text-blue-600 hover:text-blue-500"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {/* Order summary */}
            <section aria-labelledby="summary-heading" className="mt-10">
              <h2 id="summary-heading" className="sr-only">Order summary</h2>

              <div>
                <dl className="space-y-4">
                  <div className="flex items-center justify-between">
                    <dt className="text-base font-medium" style={{ color: 'var(--text-primary)' }}>Subtotal</dt>
                    <dd className="ml-4 text-base font-medium" style={{ color: 'var(--text-primary)' }}>${cartTotal.toFixed(2)}</dd>
                  </div>
                </dl>
                <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Shipping calculated at checkout.
                </p>
              </div>

              <div className="mt-10">
                <Link
                  to="/checkout"
                  className="block w-full rounded-md bg-blue-600 px-4 py-3 text-base font-medium text-center shadow hover:bg-blue-700 transition"
                  style={{ color: '#ffffff' }}
                >
                  Checkout
                </Link>
              </div>

              <div className="mt-6 text-center text-sm">
                <p>
                  or{' '}
                  <Link to="/arts" className="font-medium text-blue-600 hover:text-blue-500">
                    Continue Shopping
                    <span aria-hidden="true"> &rarr;</span>
                  </Link>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Cart;
